import React, { useEffect, useState } from 'react';

import { RingIcon, Spacing } from '@kezios/design-system';
import { Flex, Stack, Text } from '@mantine/core';

import css from './Item.module.css';

export type TextItemProps = {
  title: string;
  description: string | React.ReactNode;
  icon: React.ReactElement;
  cta?: React.ReactNode;
  titleComponent?: any;
  animatedTitle?: boolean;
};

export type ImageItemProps = {
  image: JSX.Element;
};

export type ItemProps = TextItemProps &
  ImageItemProps & { reverse?: boolean; titleComponent?: any };

const CARACTERESTOAVOID = ['-', '!'];

export const Item = ({
  title,
  description,
  image,
  icon,
  cta,
  reverse = false,
  titleComponent,
  animatedTitle,
}: ItemProps) => {
  return (
    <Flex
      className={css.container}
      gap={Spacing.s24}
      align={'center'}
      justify={'space-between'}
      wrap={reverse ? 'wrap-reverse' : 'wrap'}
    >
      {reverse && <ImageItem image={image} />}
      <TextItem
        title={title}
        description={description}
        icon={icon}
        cta={cta}
        titleComponent={titleComponent}
        animatedTitle={animatedTitle}
      />
      {!reverse && <ImageItem image={image} />}
    </Flex>
  );
};

export const TextItem = ({
  title,
  description,
  icon,
  cta,
  titleComponent,
  animatedTitle,
}: TextItemProps) => {
  const words = title.split(' ');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  const duration = Math.max(200 + words[currentWordIndex].length * 50);

  useEffect(() => {
    if (animatedTitle === false) return;
    const interval = setTimeout(() => {
      let increment = 1;
      setCurrentWordIndex(prevIndex => {
        if (CARACTERESTOAVOID.includes(words[prevIndex + 1])) {
          increment = 2;
        }
        return (prevIndex + increment) % words.length;
      });
    }, duration); // Change word every second

    return () => clearInterval(interval); // Clean up interval on component unmount
  }, [currentWordIndex]);
  return (
    <Stack className={css.child} gap={Spacing.s6}>
      <RingIcon icon={icon} color="primary" size={24} />
      <Stack gap={Spacing.s8}>
        <Stack gap={Spacing.s4}>
          {animatedTitle ? (
            <Text
              variant="displaySm.semibold"
              c="gray.9"
              component={titleComponent}
              className={css.animatedTitle}
            >
              {words.map((word, index) => (
                <div className={css.wordContainer} key={index}>
                  <span
                    key={index}
                    style={
                      index === currentWordIndex ? { color: '#FFFFFF' } : {}
                    }
                  >
                    {word}
                  </span>
                  <div
                    className={css.background}
                    //style opacity: 1; transform: scale(1); when index === currentWordIndex
                    style={
                      index === currentWordIndex
                        ? { opacity: 1, transform: 'scale(1)' }
                        : { opacity: 0, transform: 'scale(0)' }
                    }
                  />
                </div>
              ))}
            </Text>
          ) : (
            <Text
              variant="displaySm.semibold"
              c="gray.9"
              component={titleComponent}
            >
              {title}
            </Text>
          )}
          <Text variant="textLg" c={'gray.6'}>
            {description}
          </Text>
        </Stack>
        {cta}
      </Stack>
    </Stack>
  );
};

export const ImageItem = ({ image }: ImageItemProps) => {
  return (
    <Flex className={css.child} h={'100%'} align={'center'} justify={'center'}>
      {image && React.cloneElement(image, { className: css.image })}
    </Flex>
  );
};
